import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import styles from './Dashboard.module.scss'
import MarginWrapper from '../../features/wrappers/MarginWrapper'
import { LoadingIcon } from '../../features/section-components/LoadingIcon'
import JNJFooter from '../../features/jnj-footer/JNJFooter'
import { ApplicationViewPort } from '../../features/ApplicationViewPort'
import { selectUserCallStatus } from '../../features/user/userSlice'
import { getUserAppsCallStatus } from '../../features/user-apps/UserAppsSlice'
import SnackbarFeedbackOpp from '../../features/section-components/snackbarFeedbackOpp'
import { selectSurveyTemplateByTag } from '../Survey/surveyTemplateSlice'
import { SURVEY_TEMPLATE_TAG_HOME } from '../../util/config'
import { ToggleButtonGroup } from '../../features/section-components/ToggleButtonGroup'

function DashboardDesktop({
  t, isPageLoading, userObjData, userAppsData, appGridIsLoading, isSnackbarOpen,
  setSnackbarOpen, isModalOpen, setModalOpen, isGridView, setIsGridView
}) {
  const userCallStatus = useSelector(selectUserCallStatus)
  const userAppsCallStatus = useSelector(getUserAppsCallStatus)
  const surveySchema = useSelector(selectSurveyTemplateByTag)
  const buttonColors = useMemo(() => ['green', '#D7AD01', 'red'], [])
  const appListStyle = isGridView ? '' : styles['app-list']

  return (
    <MarginWrapper>
      <div className={styles['dashboard-page']}>
        <div className={styles['welcome-header']}>
          <Typography
            sx={{
              fontFamily: "'JohnsonDisplay', Arial", fontWeight: 'bolder', color: 'black', lineHeight: '2', whiteSpace: 'nowrap', fontSize: 'calc(16px + 1vw)',
            }}
          >
            {`${t('WelcomeHeader')} ${userObjData.firstName || ''}! \n`}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'JohnsonDisplay', Arial", color: 'black', lineHeight: '0', paddingBottom: 5, whiteSpace: 'nowrap', fontSize: 'calc(16px + 1vw)',
            }}
          >
            {`${t('ApplicationHeader')}`}
          </Typography>
        </div>

        <div className={styles['toggle-search-section']}>
          <div className={styles['toggle-search-container']}>
            {userAppsData.length ? (
              <ToggleButtonGroup className={styles['toggle-button-group']} falseText="List View" trueText="Grid View" value={isGridView} onClick={() => setIsGridView(!isGridView)} />
            ) : ''}
          </div>
        </div>
        <LoadingIcon isLoading={isPageLoading || userCallStatus === 'failed'}>
          {!(userAppsCallStatus === 'succeeded' && !userAppsData.length) ? (
            <div className={styles[isGridView ? 'middle-section' : 'middle-section-list']}>
              <LoadingIcon isLoading={appGridIsLoading}>
                <div className={`${styles['app-grid']} ${appListStyle}`}>
                  <ApplicationViewPort applications={userAppsData} noAppsMsg={t('NoAppsMsg')} errorContactMsg={t('ErrorContactMsg')} showLabels="true" isGridView={isGridView} />
                </div>
              </LoadingIcon>
            </div>
          ) : (
            <div className={styles['middle-section-no-apps']}>
              <ApplicationViewPort applications={userAppsData} noAppsMsg={t('NoAppsMsg')} errorContactMsg={t('ErrorContactMsg')} showLabels="true" />
            </div>
          )}
          <div>
            {surveySchema && (
              <SnackbarFeedbackOpp
                surveySchema={surveySchema}
                buttonColors={buttonColors}
                templateTag={SURVEY_TEMPLATE_TAG_HOME}
                isDelayed
                isSnackbarOpen={isSnackbarOpen}
                setSnackbarOpen={setSnackbarOpen}
                modalOpen={isModalOpen}
                setModalOpen={setModalOpen}
              />
            )}
          </div>
          <div className={styles['jnj-footer']}>
            <JNJFooter text={t('FooterText')} footerSize="medium" />
          </div>
        </LoadingIcon>
      </div>
    </MarginWrapper>
  )
}

DashboardDesktop.propTypes = {
  t: PropTypes.func.isRequired,
  isPageLoading: PropTypes.bool.isRequired,
  userObjData: PropTypes.shape({
    login: PropTypes.string,
    status: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    preferredLanguage: PropTypes.string,
    secondEmail: PropTypes.string,
    displayName: PropTypes.string,
    created: PropTypes.string.isRequired,
    lastLogin: PropTypes.string.isRequired,
  }).isRequired,
  userAppsData: PropTypes.arrayOf(PropTypes.shape({
    appId: PropTypes.string.isRequired,
    appName: PropTypes.string.isRequired,
  })).isRequired,
  appGridIsLoading: PropTypes.bool.isRequired,
  isSnackbarOpen: PropTypes.bool.isRequired,
  setSnackbarOpen: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  setModalOpen: PropTypes.func.isRequired,
  isGridView: PropTypes.bool.isRequired,
  setIsGridView: PropTypes.func.isRequired
}

export default React.memo(DashboardDesktop)
